import React from 'react';
import type {
    Card,
    CardCompare,
    CardMetric,
    CardRow,
} from '@ctd/agent-contract';
import { formatMetricValue } from '../utils/card-format';
import { hasRowCells, rowCell } from '../utils/card-rows';
import { CardFrame } from './card-frame';
import { DeltaChip } from './delta-chip';
import { MetricBlock, MetricFigures } from './metric-block';
import style from '../style.module.scss';

const Cells: React.FC<{
    metric: CardMetric;
    value: number | null;
    compare?: CardCompare;
}> = ({ metric, value, compare }) => (
    <>
        <td>{formatMetricValue({ unit: metric.unit, value })}</td>
        <td className={style.compare_previous}>
            {compare &&
                formatMetricValue({ unit: metric.unit, value: compare.value })}
        </td>
        <td>{compare && <DeltaChip metric={metric} compare={compare} />}</td>
    </>
);

const ComparisonTable: React.FC<{
    metric: CardMetric;
    rows: CardRow[];
}> = ({ metric, rows }) => (
    <div className={style.table_scroll}>
        <table className={style.compare_table} aria-label={metric.label}>
            <thead>
                <tr>
                    <th scope="col" />
                    <th scope="col">Current</th>
                    <th scope="col">Compared</th>
                    <th scope="col">Change</th>
                </tr>
            </thead>
            <tbody>
                {rows.map((row) => {
                    const cell = rowCell(row, metric.id);
                    return (
                        <tr key={row.key}>
                            <th scope="row">{row.label}</th>
                            <Cells
                                metric={metric}
                                value={cell?.value ?? null}
                                compare={cell?.compare}
                            />
                        </tr>
                    );
                })}
            </tbody>
            <tfoot>
                <tr>
                    <th scope="row">Total</th>
                    <Cells
                        metric={metric}
                        value={metric.value}
                        compare={metric.compare}
                    />
                </tr>
            </tfoot>
        </table>
    </div>
);

export const BreakdownComparisonCard: React.FC<{
    card: Extract<Card, { shape: 'breakdown_comparison' }>;
}> = ({ card }) => (
    <CardFrame card={card}>
        <div className={style.metrics}>
            {card.primary.map((metric) => (
                <MetricBlock key={metric.id} metric={metric} title={card.title}>
                    {hasRowCells(card.rows, metric) ? (
                        <ComparisonTable metric={metric} rows={card.rows} />
                    ) : (
                        <MetricFigures metric={metric} />
                    )}
                </MetricBlock>
            ))}
        </div>
    </CardFrame>
);
